import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type CardFilter, EMPTY_FILTER, type Label } from '@/entities/board';
import { Button, Dialog } from '@/shared/ui';

import { activeFilterCount } from './count';
import { LabelFilters, SelectFilters } from './FilterControls';
import styles from './FilterSheet.module.css';

interface Props {
  labels: Label[];
  filter: CardFilter;
  onApply: (filter: CardFilter) => void;
  onClose: () => void;
}

/**
 * Phone filters: every board filter in one bottom sheet. Edits are a draft until "Apply";
 * "Reset" clears the sheet's filters at once (the text search stays as it is).
 */
export function FilterSheet({ labels, filter, onApply, onClose }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(filter);
  const update = (change: (current: CardFilter) => CardFilter) => {
    setDraft(change);
  };
  const count = activeFilterCount(draft);
  return (
    <Dialog title={t('filter.sheetTitle')} onClose={onClose}>
      <p className={styles.count} role="status">
        {t('filter.activeCount', { count })}
      </p>
      <div className={styles.selects}>
        <SelectFilters filter={draft} onChange={update} variant="inline" />
      </div>
      {labels.length > 0 && (
        <section className={styles.section} aria-label={t('filter.labelsTitle')}>
          <h3 className={styles.sectionTitle}>{t('filter.labelsTitle')}</h3>
          <LabelFilters labels={labels} filter={draft} onChange={update} />
        </section>
      )}
      <div className={styles.actions}>
        <Button
          variant="secondary"
          onClick={() => {
            onApply({ ...EMPTY_FILTER, q: filter.q });
            onClose();
          }}
        >
          {t('filter.reset')}
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onApply(draft);
            onClose();
          }}
        >
          {t('filter.apply')}
        </Button>
      </div>
    </Dialog>
  );
}
